import type { ActivityEvent } from '../types';

/** Single-slot cache of the activity rows already fetched for the
 * currently-open Activity panel — set right before navigating away to a
 * shipment, read back on return so restoring doesn't re-fetch every page
 * the user had already scrolled through (10, 20 pages... one network call each).
 *
 * sessionStorage, not a plain module variable: a refresh on the shipment
 * page would otherwise wipe an in-memory cache before the user ever comes
 * back. sessionStorage survives a refresh (cleared only when the tab/window
 * closes), so the fast path still applies in that case too. A miss (empty,
 * or a bad/foreign value) falls back to re-fetching, so this is purely an
 * optimization, never a correctness dependency.
 */
interface ActivityFeedCache {
  activities: ActivityEvent[];
  activitiesPage: number;
  hasMoreActivities: boolean;
}

const STORAGE_KEY = 'zl_activity_feed_events_cache';

export function setActivityFeedCache(data: ActivityFeedCache): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable/full/blocked (e.g. strict private-browsing) —
    // the restore path's own re-fetch fallback covers this fine.
  }
}

/** Consumes the cache — a second read (e.g. a stray re-render) gets null,
 * same one-shot spirit as the router-state restore payload it travels with. */
export function takeActivityFeedCache(): ActivityFeedCache | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    return JSON.parse(raw) as ActivityFeedCache;
  } catch {
    return null;
  }
}
